import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { Presentation, Maximize2, X, Eye, Pencil, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { parseMarkdownToSlides, buildPptxBlob, SlideData } from "../../utils/buildPptx";

interface PresentationExportProps {
  text: string;
  fileName?: string;
}

const PresentationExport: React.FC<PresentationExportProps> = ({
  text,
  fileName = "presentation",
}) => {
  const [markdown, setMarkdown] = useState<string>(text);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"preview" | "edit">("preview");
  const [activeSlide, setActiveSlide] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    setMarkdown(text);
  }, [text]);

  const slides: SlideData[] = useMemo(
    () => parseMarkdownToSlides(markdown),
    [markdown]
  );

  useEffect(() => {
    if (activeSlide >= slides.length) {
      setActiveSlide(Math.max(0, slides.length - 1));
    }
  }, [slides.length, activeSlide]);

  const downloadPptx = useCallback(async () => {
    setIsDownloading(true);
    try {
      const blob = await buildPptxBlob(markdown);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generating PPTX:", err);
    } finally {
      setIsDownloading(false);
    }
  }, [markdown, fileName]);

  const goPrev = () => setActiveSlide((i) => Math.max(0, i - 1));
  const goNext = () =>
    setActiveSlide((i) => Math.min(slides.length - 1, i + 1));

  const slide = slides[activeSlide] || slides[0];

  return (
    <>
      {/* Inline card */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50/80 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <Presentation size={15} className="text-orange-500 shrink-0" />
            <span className="text-sm font-semibold text-gray-700 truncate">
              {fileName}
            </span>
            <span className="text-xs text-gray-400">
              {slides.length} slide{slides.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={downloadPptx}
              disabled={isDownloading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-60 transition-colors"
            >
              {isDownloading && <Loader2 size={13} className="animate-spin" />}
              Export PowerPoint
            </button>
            <button
              onClick={() => setIsOpen(true)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Full view"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        </div>

        {/* Inline slide preview */}
        <div className="bg-gray-100 p-4">
          <SlidePreview slide={slide} />
        </div>

        {/* Inline slide nav */}
        {slides.length > 1 && (
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50/60 border-t border-gray-200">
            <button
              onClick={goPrev}
              disabled={activeSlide === 0}
              className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs text-gray-500">
              Slide {activeSlide + 1} of {slides.length}
            </span>
            <button
              onClick={goNext}
              disabled={activeSlide >= slides.length - 1}
              className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Full-view modal */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 scrim" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative bg-white rounded-2xl text-left align-middle shadow-xl transition-all w-full lg:w-[1100px] sm:w-[90vw] h-[80vh] sm:h-[80vh] lg:h-[650px] overflow-hidden flex flex-col">
                  {/* Header */}
                  <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <Presentation size={18} className="text-orange-500 shrink-0" />
                      <h2 className="text-lg font-semibold text-gray-800 truncate">
                        {fileName}
                      </h2>
                      <span className="text-xs text-gray-400">
                        {slides.length} slide{slides.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Mode toggle */}
                      <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                        <button
                          onClick={() => setMode("preview")}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            mode === "preview"
                              ? "bg-white text-gray-800 shadow-sm"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          <Eye size={13} />
                          Preview
                        </button>
                        <button
                          onClick={() => setMode("edit")}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            mode === "edit"
                              ? "bg-white text-gray-800 shadow-sm"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          <Pencil size={13} />
                          Edit
                        </button>
                      </div>
                      <button
                        onClick={downloadPptx}
                        disabled={isDownloading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-60 transition-colors"
                      >
                        {isDownloading && <Loader2 size={13} className="animate-spin" />}
                        Export PowerPoint
                      </button>
                      <button
                        onClick={() => setIsOpen(false)}
                        className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-hidden flex">
                    {mode === "preview" ? (
                      <>
                        {/* Slide thumbnails sidebar */}
                        <div className="w-48 shrink-0 border-r border-gray-200 overflow-y-auto bg-gray-50 p-2 space-y-2">
                          {slides.map((sl, idx) => (
                            <button
                              key={idx}
                              onClick={() => setActiveSlide(idx)}
                              className={`w-full text-left rounded-md border-2 transition-colors ${
                                idx === activeSlide
                                  ? "border-orange-500"
                                  : "border-transparent hover:border-gray-300"
                              }`}
                            >
                              <div className="flex items-center gap-1.5 px-1.5 py-1">
                                <span className="text-[10px] text-gray-400 w-4 text-right">
                                  {idx + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <SlideThumb slide={sl} />
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                        {/* Main slide view */}
                        <div className="flex-1 overflow-auto bg-gray-100 p-6 flex items-center justify-center">
                          <div className="w-full max-w-3xl">
                            <SlidePreview slide={slide} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <textarea
                        value={markdown}
                        onChange={(e) => setMarkdown(e.target.value)}
                        className="w-full h-full p-4 font-mono text-[13px] leading-relaxed text-gray-800 bg-white resize-none focus:outline-none"
                        spellCheck={false}
                        placeholder="Write slides as markdown. Use '## Slide Title' to start a new slide, then '-' for bullets..."
                      />
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

// ─── Slide rendering helpers ──────────────────────────────────────

const SlidePreview: React.FC<{ slide: SlideData | undefined }> = ({ slide }) => {
  if (!slide) return null;
  return (
    <div
      className="bg-white rounded-lg shadow-md mx-auto overflow-hidden"
      style={{ aspectRatio: "16 / 9", maxWidth: "100%" }}
    >
      <div className="h-full w-full p-8 flex flex-col">
        {slide.title && (
          <>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{slide.title}</h3>
            <div className="w-full h-[3px] bg-orange-500 mb-4" />
          </>
        )}
        <div className="flex-1 overflow-hidden text-gray-800 text-sm space-y-1.5">
          {slide.elements.map((el, idx) => renderElement(el, idx))}
        </div>
      </div>
    </div>
  );
};

const SlideThumb: React.FC<{ slide: SlideData }> = ({ slide }) => (
  <div
    className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden"
    style={{ aspectRatio: "16 / 9" }}
  >
    <div className="h-full w-full p-1.5">
      <div className="text-[8px] font-bold text-gray-900 truncate leading-tight">
        {slide.title || "Untitled"}
      </div>
      <div className="w-3 h-[1.5px] bg-orange-500 rounded mt-0.5" />
      <div className="text-[6px] text-gray-500 mt-1 space-y-0.5">
        {slide.elements.slice(0, 4).map((el, idx) => (
          <div key={idx} className="truncate">
            {el.kind === "bullet" ? `• ${el.text}` : el.kind === "table" ? "[table]" : el.text}
          </div>
        ))}
      </div>
    </div>
  </div>
);

function renderElement(el: SlideData["elements"][number], idx: number): React.ReactNode {
  if (el.kind === "subheading") {
    return (
      <h4 key={idx} className="text-base font-semibold text-orange-600 mt-2">
        {el.text}
      </h4>
    );
  }
  if (el.kind === "bullet") {
    return (
      <div
        key={idx}
        className="flex gap-2"
        style={{ paddingLeft: `${el.level * 16}px` }}
      >
        <span className="text-orange-500 shrink-0">•</span>
        <span
          className={`${el.bold ? "font-semibold" : ""} ${el.italic ? "italic" : ""}`}
        >
          {el.text}
        </span>
      </div>
    );
  }
  if (el.kind === "table") {
    return (
      <table key={idx} className="w-full border-collapse my-2 text-xs">
        <tbody>
          {el.rows.map((row, rIdx) => (
            <tr key={rIdx}>
              {row.map((cell, cIdx) =>
                el.hasHeader && rIdx === 0 ? (
                  <th
                    key={cIdx}
                    className="border border-gray-300 bg-gray-700 text-white px-2 py-1 text-left font-semibold"
                  >
                    {cell}
                  </th>
                ) : (
                  <td
                    key={cIdx}
                    className={`border border-gray-300 px-2 py-1 ${
                      rIdx % 2 === 0 ? "bg-gray-50" : ""
                    }`}
                  >
                    {cell}
                  </td>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <p
      key={idx}
      className={`${el.bold ? "font-semibold" : ""} ${el.italic ? "italic" : ""}`}
    >
      {el.text}
    </p>
  );
}

export default PresentationExport;
